/*eslint-disable @typescript-eslint/no-unused-vars*/

import React, { useCallback, useMemo, useRef, useState } from 'react';
import './DocumentationDashboard.css'
import { CustomBreadCrumbs, CustomTooltipWithLabel, FilterLayout, FilterProp, formatRelativeTimeForTable, renderHeaderActionButtons, SidePopover, TableDashboard, useAppDispatch, useAppSelector } from '../../../globalUtils/globalExports';
import { fetchRmsSupportDocumentationListData, getCurrentFiltersValues, getCurrentScreenAppliedFiltersCount, getFilterReducer, getFilterSelectedValue, getReferenceDataReducer, getRmsSupportCenterReducer, setFilterData, setRmsSupportDocumentationTableProps } from '../../../rmsReduxStore/reduxExports';
import { cloneDeep } from 'lodash';
import { createBreadCrumbProps, downloadFile } from '../../../globalUtils/globalHooks';
import { useNavigate } from 'react-router-dom';
import { IconWithTooltip } from '../../../globalUtils/globalIcons';
import { Id, toast } from 'react-toastify';
import { width } from '@mui/system';



const DocumentationDashboard: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {rmsSupportDocumentationTableProps,rmsSupportDocumentationList,rmsSupportDocumentationListCount,rmsSupportDocumentationListLoader} = useAppSelector(getRmsSupportCenterReducer);
    const {rmsSupportDocumentVersionReferenceData} = useAppSelector(getReferenceDataReducer);
    const rmsSupportDocumentationTablePropsRef = useRef(rmsSupportDocumentationTableProps);
    const [filtersVisible, setFiltersVisible] = useState(false);
    const {userReferenceData} = useAppSelector(getReferenceDataReducer);
    const { screenFilters } = useAppSelector(getFilterReducer)


    const layoutCallback = useCallback(
        async (
          pageNumber: number,
          pageSize: number,
          view: string,
          field: string | null,
          order: string,
          filterData,
          searchTerm: string,
        ) => {
          const tableProps = cloneDeep(rmsSupportDocumentationTablePropsRef.current);
          const rmsSupportDocumentationFilter = getCurrentFiltersValues('rmsSupportDocumentation', true);
          const appliedFilters = filterData ? filterData : rmsSupportDocumentationFilter;
    
          await dispatch(
            fetchRmsSupportDocumentationListData({
              view: view ?? 'list',
              sortBy: field || tableProps?.sortBy,
              order: order || tableProps?.order,
              pageSize: pageSize,
              pageNumber: pageNumber,
              filters: appliedFilters,
              searchTerm: searchTerm != null ? searchTerm : tableProps?.searchTerm,
            })
          );
    
          if (tableProps) {
            tableProps.view = view || 'list'
            tableProps.sortBy = field || rmsSupportDocumentationTablePropsRef?.current?.sortBy;
            tableProps.order = order || rmsSupportDocumentationTablePropsRef?.current?.order;
            tableProps.pageNumber = pageNumber;
            tableProps.pageSize = pageSize;
            if (searchTerm != null && searchTerm != undefined) {
              tableProps.searchTerm = searchTerm;
            }
          }
          await dispatch(setRmsSupportDocumentationTableProps(tableProps));
        },
        [rmsSupportDocumentationTablePropsRef?.current, dispatch]
      );


    const onChangeOfSearchText = useCallback(async(searchText: string)=>{
        const trimmedSearchText = searchText.trim();
        const tableProps = cloneDeep(rmsSupportDocumentationTablePropsRef.current);
        if (tableProps) {
            tableProps.searchTerm = trimmedSearchText;
        }
        const rmsSupportDocumentationFilter = getCurrentFiltersValues('rmsSupportDocumentation', true);
        await layoutCallback(
            1,
            tableProps?.pageSize,
            tableProps?.view,
            tableProps?.sortBy,
            tableProps?.order,
            rmsSupportDocumentationFilter,
            trimmedSearchText,
        )
    },[rmsSupportDocumentationTablePropsRef?.current, layoutCallback])

    const searchBoxConfiguration = useMemo(() => {
        return {
            searchBoxClassName: 'rms__support__documentation__dashboard__search__box',
            searchFieldId: 'rms-support-documentation-dashboard-search-box',
            searchFieldName: 'documentation-dashboard-search-box',
            handleSearch: onChangeOfSearchText,
        };
    }, [onChangeOfSearchText, rmsSupportDocumentationTablePropsRef?.current]);


    const fileDownloadSuccessToast = useCallback((): Id => {
        return toast.success('File downloaded.');
    }, []);

    const fileDownloadFailureToast = useCallback((): Id => {
        return toast.warn('Something went wrong. Please download again.');
      }, []);

    const onIconClick = (rowData,mode) => {
        navigate('/add-document',{
            state: {
                mode: mode,
                documentData: {
                    id: rowData?.id,
                    name: rowData?.name,
                    version: rowData?.version,
                    documentType: rowData?.documentType,
                    description: rowData?.description,
                    uploadedDocument: rowData?.uploadedDocument
                }
            }
        })
    }

    const onDownloadFileIconClick = (rowData) => {
        if(!rowData?.uploadedDocument) {
            fileDownloadFailureToast()
            return;
        }
        downloadFile({ url: rowData?.uploadedDocument });
        fileDownloadSuccessToast()
    }


    const actionIconsList = useCallback((documentType)=>{
        return [
        {
            icon: 'rms__view__icon',
            tooltipText:'view',
            fieldId:'rms-documentation-view-btn',
            className:'rms__documentation__view__icon__wrap',
            isAccessible: documentType !== 'SW Release',
            action: (rowData)=>onIconClick(rowData,'view')
        },
        {
            icon: 'rms__documentation__download__icon',
            tooltipText: 'Download',
            fieldId:'rms-support-documentation-download-btn',
            className:'rms-support-documentation-download__icon__wrap',
            isAccessible: true,
            action: onDownloadFileIconClick
        },
        {
            icon: 'rms__pencil__icon',
            tooltipText:'Edit',
            fieldId:'rms-documentation-edit-btn',
            className:'rms__documentation__edit__icon__wrap',
            isAccessible: documentType !== 'SW Release',
            action: (rowData)=>onIconClick(rowData,'edit')
        }
      ]
    },[])
      


    const rmsSupportDocumentationTableColumns = [
        {
            field: 'name',
            headerName: 'Document Name',
            flex: 1,
            minWidth: 250,
            sortable: true,
            renderCell: (params) => {
                const rowData = params?.row; 
                const isSwRelease = rowData?.documentType==='SW Release'; 
                return (
                    <CustomTooltipWithLabel label={rowData?.name} labelId={'docuemnt-name-'+rowData?.id} containerClassName={isSwRelease ? 'rms__support__documentation__name-disabled' :'rms__support__documentation__name'} tooltipClickHandler={ !isSwRelease ? ()=>onIconClick(rowData,'view') : ()=>{}}/>
                )
            }
        },
        {
            field: 'documentType',
            headerName: 'Document Type',
            flex: 1,
            minWidth:100,
            sortable: true,
            renderCell: (params) => {
                const rowData = params?.row;
                let backgroundColor = '';
                switch(rowData?.documentType){
                    case 'Release Notes':
                        backgroundColor = '#E7531DBF'; 
                        break;
                    case 'SW Release':
                        backgroundColor = '#8F8DFF'; 
                        break;
                    case 'Protocol Sheet':
                        backgroundColor = '#438EF7';
                        break;
                }
                return (
                    <span 
                        style={
                            {
                                backgroundColor,
                                color: '#fff',
                                fontSize: '12px',
                                letterSpacing:'-1.6%',
                                borderRadius: '6px',
                                padding:'2px 4px'
                            }
                        }
                    >{rowData?.documentType}</span>
                )
            }

        },
        {
            field: 'version',
            headerName: 'Version',
            flex: 1,
            minWidth: 100,
            sortable: true,
            renderCell: (params) => {
                const rowData = params?.row; 
                return (
                    <CustomTooltipWithLabel label={rowData?.version} labelId={'docuemnt-version-'+rowData?.id} />
                )
            }
        },
        {
            field: 'description',
            headerName:'Description',
            flex: 1,
            minWidth: 150,
            sortable: false,
            renderCell: (params) => {
                const rowData = params?.row; 
                return (
                    <CustomTooltipWithLabel label={rowData?.description} labelId={'docuemnt-description-'+rowData?.id} />
                )
            }
        },
        {
            field: 'createdBy',
            headerName: 'Created By',
            flex: 1,
            minWidth: 100,
            sortable: true,
            renderCell: (params) => {
                const rowData = params?.row; 
                return (
                    <CustomTooltipWithLabel label={rowData?.createdBy} labelId={'docuemnt-uploaded-by-'+rowData?.id} />
                )
            }
        },
        {
            field: 'createdAt',
            headerName: 'Created At',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => {
                return (
                    <span style={{ fontWeight: 400 }}>{formatRelativeTimeForTable(params.value)}</span>
                )
            }
        },
        {
            field: 'updatedBy',
            headerName: 'Updated By',
            flex: 1,
            minWidth: 100,
            sortable: true,
            renderCell: (params) => {
                const rowData = params?.row; 
                return (
                    <CustomTooltipWithLabel label={rowData?.updatedBy} labelId={'docuemnt-uploaded-by-'+rowData?.id} />
                )
            }
        },
        {
            field: 'updatedAt',
            headerName: 'Updated At',
            flex: 1,
            minWidth: 150,
            sortable: true,
            renderCell: (params) => {
                return (
                    <span style={{ fontWeight: 400 }}>{formatRelativeTimeForTable(params.value)}</span>
                )
            }
        },
        {
              field: 'action',
              headerName: 'Action',
              flex: 1,
              minWidth: 100,
              sortable: false,
              pinnable: false,
              renderCell: (params) => {
                const rowData = params?.row;
                const documentType = rowData?.documentType
                return (
                  <div className='rms__support__documentation__action__icon__wrap'>
                    {
                      actionIconsList(documentType).map(actionBtn=>{
                        if(!actionBtn?.isAccessible)return;
                        return (
                          <div key={actionBtn.fieldId} className={actionBtn.className} onClick={(e)=>actionBtn.action(rowData)}>
                            <IconWithTooltip  iconClassName={actionBtn.icon} toolTipText={actionBtn.tooltipText}/>
                          </div>
                        )
                      })
                    }
                  </div>
                )
              }
            }
    ]

    const getTableRows = useCallback((): IRmsSupportDocumentationItem[] => {
        return rmsSupportDocumentationList !== null && rmsSupportDocumentationList?.length > 0 ? rmsSupportDocumentationList : []
      }, [rmsSupportDocumentationList])

    const headerActionBtns = renderHeaderActionButtons({
        actionBtns: 
            [
                {
                    id:'add_new_btn',
                    isAccessible: true,
                    headerActionBtnClick: (): void => {
                        navigate('/add-document')
                    }
                },
                {
                    id: 'add_filter_btn',
                    isAccessible: true,
                    headerActionBtnClick: (): void => {
                        setFiltersVisible(true);
                    },
                    badgeCount: getCurrentScreenAppliedFiltersCount('rmsSupportDocumentation',true)
                }
            ]
    })

    const breadCrumbArray = createBreadCrumbProps(
            {
                breadCrumbProps: [
                    {
                        objectType: 'link',
                        id: 'supportCentre',
                    },
                    {
                        objectType: 'text',
                        id:'text',
                        text: 'Documentation'
                    }
                ]
            }
        )

    const handleFiltersClosePopOver = (): void => {
        setFiltersVisible(false);
    }

    const handleFilterDataChange = useCallback(async (data, filterKey: string, isGlobal: boolean) => {
        const tableProps = cloneDeep(rmsSupportDocumentationTablePropsRef.current);
        const res = await setFilterData(data, filterKey, 'rmsSupportDocumentation' , 'SET', isGlobal, true)(dispatch);
        if (res?.message === 'Action dispatched successfully') {
          await layoutCallback?.(
            1,
            tableProps?.pageSize,
            tableProps?.view,
            tableProps?.sortBy,
            tableProps?.order,
            res?.filters,
            tableProps?.searchTerm ?? '',
          )
        }
      }, [rmsSupportDocumentationTablePropsRef?.current]);


    const rmsSupportDocumentationFilterProps : FilterProp[] = useMemo(()=> {
        const documentUploadedDateValue = getFilterSelectedValue('rmsSupportUploadedDateInterval', 'rmsSupportDocumentation', false);
        const documentUpdatedAtValue = getFilterSelectedValue('rmsSupportDocumentUpdatedAtInterval', 'rmsSupportDocumentation', false);
        return [
            {
                filterLabel: 'Document Type',
                filterId: 'rmsSupportDocumentType',
                filterType: 'dropdown',
                filterDropdownProps: {
                    selectValue: getFilterSelectedValue('rmsSupportDocumentType', 'rmsSupportDocumentation', false),
                    selectOptions: ['Release Notes','SW Release','Protocol Sheet']?.map(item=>({id:item,name:item,label:item,value:item})),
                    handleSelectOptionChange: (val) : void => {
                        handleFilterDataChange(val,'rmsSupportDocumentType',false);
                    },
                    selectDropDownId: 'rms-support-documentation-document-type-filter-dropdown',
                    isMultiSelect: true
                }
            },
            {
                filterLabel: 'Version',
                filterId: 'rmsSupportDocumentVersion',
                filterType: 'dropdown',
                filterDropdownProps: {
                    selectValue: getFilterSelectedValue('rmsSupportDocumentVersion', 'rmsSupportDocumentation', false),
                    selectOptions: rmsSupportDocumentVersionReferenceData ?? [],
                    handleSelectOptionChange: (val) : void => {
                        handleFilterDataChange(val,'rmsSupportDocumentVersion',false);
                    },
                    selectDropDownId: 'rms-support-documentation-version-filter-dropdown',
                    isMultiSelect: true
                }
            },
            {
                filterLabel: 'Created By',
                filterId: 'rmsSupportUploadedBy',
                filterType: 'dropdown',
                filterDropdownProps: {
                    selectValue: getFilterSelectedValue('rmsSupportUploadedBy', 'rmsSupportDocumentation', false),
                    selectOptions: userReferenceData ?? [],
                    handleSelectOptionChange: (val) : void => {
                        handleFilterDataChange(val,'rmsSupportUploadedBy',false);
                    },
                    selectDropDownId: 'rms-support-documentation-uploaded-by-filter-dropdown',
                    isMultiSelect: true
                } 
            },
            {
                filterLabel: 'Updated By',
                filterId: 'rmsSupportDocumentUpdatedBy',
                filterType: 'dropdown',
                filterDropdownProps: {
                    selectValue: getFilterSelectedValue('rmsSupportDocumentUpdatedBy', 'rmsSupportDocumentation', false),
                    selectOptions: userReferenceData ?? [],
                    handleSelectOptionChange: (val) : void => {
                        handleFilterDataChange(val,'rmsSupportDocumentUpdatedBy',false);
                    },
                    selectDropDownId: 'rms-support-documentation-updated-by-filter-dropdown',
                    isMultiSelect: true
                } 
            },
            {
                filterLabel: 'Created At',
                filterId: 'rmsSupportUploadedDateInterval',
                filterType: 'dateFilter',
                dateFilterProps: {
                datePickerId: 'rms-support-document-upload-date-picker',
                datePickerClassName: '',
                selectsRange: true,
                startDate: documentUploadedDateValue?.[0]
                    ? new Date(documentUploadedDateValue?.[0])
                    : null,
                endDate: documentUploadedDateValue?.[1]
                    ? new Date(documentUploadedDateValue?.[1])
                    : null,
                onChange: (val): void => {
                    handleFilterDataChange(val,'rmsSupportUploadedDateInterval', false);
                },
                }
            },
            {
                filterLabel: 'Updated At',
                filterId: 'rmsSupportDocumentUpdatedAtInterval',
                filterType: 'dateFilter',
                dateFilterProps: {
                datePickerId: 'rms-support-document-updated-at-date-picker',
                datePickerClassName: '',
                selectsRange: true,
                startDate: documentUpdatedAtValue?.[0]
                    ? new Date(documentUpdatedAtValue?.[0])
                    : null,
                endDate: documentUpdatedAtValue?.[1]
                    ? new Date(documentUpdatedAtValue?.[1])
                    : null,
                onChange: (val): void => {
                    handleFilterDataChange(val,'rmsSupportDocumentUpdatedAtInterval', false);
                },
                }
            }
        ]
    },[handleFilterDataChange,screenFilters,userReferenceData,rmsSupportDocumentVersionReferenceData])

    const onResetButtonClick = useCallback(async (callList: boolean) => {
        const res = await setFilterData(null, '', 'rmsSupportDocumentation', 'CLEAR', true)(dispatch);
        if (res?.message === 'Action dispatched successfully') {
          // Reset the filters
          if (callList && rmsSupportDocumentationTablePropsRef?.current) {
            const tableProps = cloneDeep(rmsSupportDocumentationTablePropsRef?.current);
            await layoutCallback(
              1,
              tableProps?.pageSize,
              tableProps?.view,
              tableProps?.sortBy,
              tableProps?.order,
              res?.filters,
              tableProps?.searchTerm ?? '',
            )
          }
        }
    
    }, [rmsSupportDocumentationTablePropsRef?.current])

    const filterFooterBtns = useMemo(() => {
    return [
        {
        buttonText: 'Reset',
        buttonId: 'Reset',
        btnClassName: 'secondary__btn',
        handleClick: (): Promise<void> => onResetButtonClick(true),
        isDisabled: false,
        buttonVariant: 'outlined',
        },
    ];
    }, [onResetButtonClick]);
    

    return (
        <>
            <TableDashboard
                layoutsEnabled={true}
                tableId='rms-support-documentation-dashboard-table'
                tableClassName='rms__support__documentation__dashboard__table'
                header='Documents'
                searchBoxIncluded={true}
                searchBoxConfiguration={searchBoxConfiguration}
                gridColumns={rmsSupportDocumentationTableColumns}
                tableRows={getTableRows()}
                layoutView='list'
                headerActionBtns={headerActionBtns}
                listLayoutCallBack={layoutCallback}
                totalCount={rmsSupportDocumentationListLoader ? 0 : rmsSupportDocumentationListCount}
                showSkeleton={!rmsSupportDocumentationList}
                totalCountText={'documents'}
                showLoader={rmsSupportDocumentationListLoader}
                pageNumber={rmsSupportDocumentationTableProps?.pageNumber}
                tablePageSize={rmsSupportDocumentationTableProps?.pageSize}
                sortField={rmsSupportDocumentationTableProps?.sortBy}
                sortOrder={rmsSupportDocumentationTableProps?.order}
                showRefreshButton={false}
                breadCrumbs={breadCrumbArray}
                showBreadCrumb={true}
            />
            <>
                {
                    filtersVisible && <SidePopover
                    id={'rms-support-documentation-filters'}
                    open={filtersVisible}
                    containerClassName={'rms-support-documentation-side-filters'}
                    handleClose={handleFiltersClosePopOver}
                    headerText={'Add Filters'}
                    // eslint-disable-next-line react/no-children-prop
                    children={<FilterLayout requiredReferenceDataCallbacks={['user','rmsSupportDocumentVersion']}
                        filters={rmsSupportDocumentationFilterProps} handleFilterDataChange={handleFilterDataChange}
                        excludeGlobalFilters={true}
                    />}
                    btnsList={filterFooterBtns}
                    />
                }
            </>
        </>
    )
}



export default DocumentationDashboard;